import { useMentionBoostEligibilityQuery } from '@/hooks/queries/use-mention-boost-eligibility-query';
import {
  mentionBoostSetupQueryKey,
  useMentionBoostSetupQuery,
} from '@/hooks/queries/use-mention-boost-setup-query';
import { useRequestMentionBoostAuthorization } from '@/hooks/actions/use-request-mention-boost-authorization';
import { Alert } from '@/components/ui/alert';
import { BoostAuthorizationHandshake, type BoostIdentity } from './BoostAuthorizationHandshake';
import { BoostStatusInline } from './BoostStatusInline';
import { BoostFooter, BOOST_STATE_ENTER_ANIMATION } from './BoostFooter';
import { resolveDeliveryFooterIntent, resolveEligibilityFooterIntent } from './boost-footer-intent';
import { MentionBoostDisplayRightsRow } from './MentionBoostDisplayRightsRow';
import { MentionBoostProposalStep } from './MentionBoostProposalStep';
import { MentionBoostRequestPanel } from './MentionBoostRequestPanel';
import { MentionBoostSetupPanel } from './MentionBoostSetupPanel';
import { metaStateFromAuthorizationStatus } from '@/components/display-rights/display-rights-status';
import { StopBoostConfirmDialog } from './StopBoostConfirmDialog';
import { useBoostStopFlow } from './useBoostStopFlow';
import { isBoostRelaunchable } from './boost-setup/useMentionBoostLaunch';
import { ConnectMetaAdsButton } from './boost-setup/ConnectMetaAdsButton';
import { Badge, Box, Flex, Heading, HStack, Image, Skeleton, Text, VStack } from '@chakra-ui/react';
import {
  type MentionBoostEligibilityBlocker,
  MentionBoostEligibilityBlockerCode,
  MentionBoostEligibilityStatus,
  MentionBoostAuthorizationScope,
  MentionBoostDeliveryState,
} from '@fluenceur/types';
import { useQueryClient } from '@tanstack/react-query';
import { WarningTriangle } from 'iconoir-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type BoostBadgeKey =
  | 'blocked'
  | 'ready'
  | 'permissionNeeded'
  | 'pending'
  | 'declined'
  | 'revoked'
  | 'expired'
  | 'failed'
  | 'loading';
type BoostCopyKey =
  | 'error'
  | 'blocked'
  | 'ready'
  | 'permissionNeeded'
  | 'pending'
  | 'declined'
  | 'revoked'
  | 'expired'
  | 'failed';
type BoostPanelTone = 'orange' | 'primary' | 'gray' | 'blue' | 'green';
type MentionBoostPanelParts = {
  footer: ReactNode;
  isRequestOpen: boolean;
  isSetupOpen: boolean;
  // A launched (non-draft) boost drives the inline paid-stats layout, the quiet
  // "View on Meta" header link, and the boost-first ordering of the pane.
  isBoosted: boolean;
  // Launch timestamp of the launched boost, used to mark "from here, we boosted"
  // on the organic timeline. Null when no launched boost exists.
  boostLaunchedAt: string | null;
  onCloseRequest: () => void;
  onCloseSetup: () => void;
  onCloseProposal: () => void;
  panel: ReactNode;
  requestPanel: ReactNode;
  setupPanel: ReactNode;
  // Non-null exactly when the optional display-rights step is showing: a
  // separate boolean was a second way to say the same thing, and two ways to
  // say one thing is one way to disagree.
  proposalPanel: ReactNode;
};

interface MentionBoostPanelProps {
  mentionId: string;
  // The brand + creator identity shown in the pending-authorization handshake.
  identity?: BoostIdentity;
  children?: (parts: MentionBoostPanelParts) => ReactNode;
  onSetupOpenChange?: (isOpen: boolean) => void;
}

const BLOCKER_COLOR: Record<MentionBoostEligibilityBlockerCode, 'orange' | 'gray'> = {
  [MentionBoostEligibilityBlockerCode.UNSUPPORTED_PLATFORM]: 'gray',
  [MentionBoostEligibilityBlockerCode.STORY_NOT_BOOSTABLE]: 'gray',
  [MentionBoostEligibilityBlockerCode.MEDIA_EXPIRED]: 'orange',
  [MentionBoostEligibilityBlockerCode.MEDIA_UNAVAILABLE]: 'orange',
  [MentionBoostEligibilityBlockerCode.MEDIA_REFERENCE_MISSING]: 'orange',
  [MentionBoostEligibilityBlockerCode.INSTAGRAM_INTEGRATION_MISSING]: 'orange',
  [MentionBoostEligibilityBlockerCode.META_PERMISSIONS_MISSING]: 'orange',
  [MentionBoostEligibilityBlockerCode.OFFICIAL_META_PATH_UNAVAILABLE]: 'orange',
};

// Single source of truth for how each eligibility status presents: badge label,
// copy block, and panel tone. Pending is informational blue (an in-flight wait,
// request sent, awaiting the creator), distinct from the inert/ended gray states.
const STATUS_PRESENTATION: Record<
  MentionBoostEligibilityStatus,
  { badge: BoostBadgeKey; copy: BoostCopyKey; tone: BoostPanelTone }
> = {
  [MentionBoostEligibilityStatus.BLOCKED]: { badge: 'blocked', copy: 'blocked', tone: 'orange' },
  [MentionBoostEligibilityStatus.READY]: { badge: 'ready', copy: 'ready', tone: 'green' },
  [MentionBoostEligibilityStatus.PERMISSION_NEEDED]: {
    badge: 'permissionNeeded',
    copy: 'permissionNeeded',
    tone: 'primary',
  },
  [MentionBoostEligibilityStatus.PENDING]: { badge: 'pending', copy: 'pending', tone: 'blue' },
  [MentionBoostEligibilityStatus.DECLINED]: { badge: 'declined', copy: 'declined', tone: 'orange' },
  [MentionBoostEligibilityStatus.REVOKED]: { badge: 'revoked', copy: 'revoked', tone: 'orange' },
  [MentionBoostEligibilityStatus.EXPIRED]: { badge: 'expired', copy: 'expired', tone: 'gray' },
  [MentionBoostEligibilityStatus.FAILED]: { badge: 'failed', copy: 'failed', tone: 'orange' },
};

interface EligibilityPresentation {
  badgeKey: BoostBadgeKey;
  copyKey: BoostCopyKey;
  panelTone: BoostPanelTone;
}

/**
 * Resolve badge/copy/tone for an eligibility status. The undefined-status
 * fallback covers loading (no data) and the permission-needed default; an error
 * forces the error copy.
 */
function getEligibilityPresentation(
  status: MentionBoostEligibilityStatus | undefined,
  hasData: boolean,
  isError: boolean
): EligibilityPresentation {
  const base = status
    ? STATUS_PRESENTATION[status]
    : {
        badge: (hasData ? 'permissionNeeded' : 'loading') as BoostBadgeKey,
        copy: 'permissionNeeded' as BoostCopyKey,
        tone: (hasData ? 'primary' : 'gray') as BoostPanelTone,
      };

  return {
    badgeKey: base.badge,
    copyKey: isError ? 'error' : base.copy,
    panelTone: base.tone,
  };
}

export function MentionBoostPanel({
  mentionId,
  identity,
  children,
  onSetupOpenChange,
}: MentionBoostPanelProps) {
  const [isRequestOpen, setIsRequestOpen] = useState(false);
  const [isSetupOpen, setIsSetupOpen] = useState(false);
  // Set only once the authorization request has succeeded, from the id the
  // response carries: the proposal step has nothing to attach to before that.
  const [proposalAuthorizationId, setProposalAuthorizationId] = useState<string | null>(null);
  const [limitToMention, setLimitToMention] = useState(false);
  const queryClient = useQueryClient();
  const { isPending: isRequestingAuthorization, mutate: requestAuthorization } =
    useRequestMentionBoostAuthorization();
  const stopFlow = useBoostStopFlow(mentionId);
  const { data, isLoading, isError, dataUpdatedAt } = useMentionBoostEligibilityQuery({
    mentionId,
  });
  // The setup query carries the launched boost. When one exists, the surface
  // routes to the launched (read-only) view instead of the editable setup form
  // or the preflight, so reopening the modal shows delivery state directly.
  const { data: setupData } = useMentionBoostSetupQuery({ mentionId });
  // A launched boost's delivery state is what matters once it exists: it is shown
  // inline in place of the eligibility panel and footer, not as a separate
  // full-screen takeover.
  const launchedBoost = setupData?.launchedBoost ?? null;

  // The eligibility status is taken as the server reports it. A stale
  // authorization (one carrying no Meta identifiers) already arrives as
  // `expired`, and a blocked mention as `blocked` — the server resolves that
  // precedence, so restating any of it here could only ever disagree with it.
  const isBlocked = data?.status === MentionBoostEligibilityStatus.BLOCKED;
  // A story is refused by its format: there is no media to fix, no permission to
  // ask for and no setup to open. The surface says it once, quietly, and offers
  // nothing else.
  const isStoryNotBoostable = !!data?.blockers.some(
    (blocker) => blocker.code === MentionBoostEligibilityBlockerCode.STORY_NOT_BOOSTABLE
  );
  const canOpenPreflight = !!data?.canRequestAuthorization && !!data?.meta.canManageBoost;
  const contentOnlyOptionAvailable = data?.authorization.contentOnlyOptionAvailable ?? false;
  const canOpenSetup =
    data?.status === MentionBoostEligibilityStatus.READY && !!data.meta.canManageBoost;
  const { badgeKey, copyKey, panelTone } = getEligibilityPresentation(
    data?.status,
    !!data,
    isError
  );
  const handleBoostClick = useCallback(() => {
    if (canOpenSetup) {
      setIsSetupOpen(true);
      return;
    }

    setIsRequestOpen(true);
  }, [canOpenSetup]);
  const handleCloseRequest = useCallback(() => setIsRequestOpen(false), []);
  const handleCloseSetup = useCallback(() => setIsSetupOpen(false), []);
  const handleCloseProposal = useCallback(() => setProposalAuthorizationId(null), []);
  // A launched (non-draft) boost leads the pane with its status + inline figures.
  const isBoosted =
    !!launchedBoost && launchedBoost.deliveryState !== MentionBoostDeliveryState.DRAFT;
  // The Meta request itself is untouched: same endpoint, same payload, same
  // effects. The display-rights step is offered only AFTER it has succeeded,
  // and only when the response names the authorization it would attach to.
  const handleRequestAuthorization = useCallback(() => {
    requestAuthorization(
      {
        mentionId,
        scope: limitToMention
          ? MentionBoostAuthorizationScope.CONTENT_LEVEL
          : MentionBoostAuthorizationScope.ACCOUNT_LEVEL,
      },
      {
        onSuccess: (response) => {
          setIsRequestOpen(false);
          setProposalAuthorizationId(response.data?.authorization.id ?? null);
        },
      }
    );
  }, [limitToMention, mentionId, requestAuthorization]);

  useEffect(() => {
    if (!contentOnlyOptionAvailable && limitToMention) {
      setLimitToMention(false);
    }
  }, [contentOnlyOptionAvailable, limitToMention]);

  // The host disables prev/next nav while a takeover pane (setup, request or the
  // display-rights step) is mounted, so the marketer cannot scroll to another
  // mention mid-flow.
  const isProposalOpen = !!proposalAuthorizationId;
  useEffect(() => {
    onSetupOpenChange?.(isSetupOpen || isRequestOpen || isProposalOpen);
  }, [isProposalOpen, isRequestOpen, isSetupOpen, onSetupOpenChange]);

  // The displayed delivery state comes from the SETUP query (`launchedBoost`),
  // but it is the ELIGIBILITY endpoint that triggers the server-side boost sync.
  // So whenever a fresh eligibility fetch lands (its `dataUpdatedAt` advances),
  // the server has just reconciled the boost: invalidate the setup query so the
  // shown state reflects that sync instead of a stale prior visit.
  //
  // This cannot loop: the effect keys on the eligibility query's `dataUpdatedAt`,
  // and invalidating the SETUP query never changes the eligibility query's
  // timestamp. The ref guard makes it fire once per fresh eligibility fetch, not
  // on every render. `dataUpdatedAt` is 0 until the first fetch settles, so the
  // initial mount is covered too.
  const lastSyncedEligibilityAt = useRef<number | null>(null);
  useEffect(() => {
    if (!mentionId || dataUpdatedAt === 0) {
      return;
    }
    if (lastSyncedEligibilityAt.current === dataUpdatedAt) {
      return;
    }
    lastSyncedEligibilityAt.current = dataUpdatedAt;
    queryClient.invalidateQueries({ queryKey: mentionBoostSetupQueryKey(mentionId) });
  }, [dataUpdatedAt, mentionId, queryClient]);

  // The Meta half of the crossed state. Absent until an authorization exists —
  // there is no Instagram answer to report before one has been asked for. Also
  // absent on a stale authorization: `stale` explains that no Meta identifiers
  // back the row, so its stored status would report an Instagram answer that
  // was never given.
  const metaState =
    data?.authorization.current && !data.authorization.current.stale
      ? metaStateFromAuthorizationStatus(data.authorization.current.status)
      : undefined;

  // Keying the animated wrapper on the resolved state replays the entrance only
  // when the decision surface actually swaps (launched delivery state, or an
  // eligibility/badge change), not on every render.
  const panelStateKey = launchedBoost
    ? `delivery:${launchedBoost.deliveryState}`
    : `badge:${badgeKey}`;
  const panel = isStoryNotBoostable ? (
    <MentionBoostStoryNotice />
  ) : (
    <Box key={panelStateKey} animation={BOOST_STATE_ENTER_ANIMATION}>
      {launchedBoost ? (
        // Every launched boost shares one inline view: a compact status row, the
        // repair card for failed / needs-attention, and the Meta-modeled paid
        // performance (summary + metric grid) inline. Draft renders nothing.
        <BoostStatusInline
          mentionId={mentionId}
          deliveryState={launchedBoost.deliveryState}
          lastErrorCode={launchedBoost.lastErrorCode}
        />
      ) : (
        <MentionBoostContent
          badgeKey={badgeKey}
          blockers={data?.blockers ?? []}
          copyKey={copyKey}
          identity={identity}
          isLoading={isLoading}
          panelTone={panelTone}
          showAdminWarning={!!data && !data.meta.canManageBoost && !isBlocked}
          // Only where it is actionable: a mention already blocked for another
          // reason, or a viewer who could not request anyway, gains nothing
          // from being told to connect an ad account.
          showAdsNotConnected={
            !!data && !data.meta.hasAdsIntegration && !isBlocked && data.meta.canManageBoost
          }
        />
      )}

      {/* Outside the branch on purpose: the compensation state belongs to the
          authorization, not to whether a boost happens to be running. Inside
          either branch, the other one would silently drop it. */}
      <MentionBoostDisplayRightsRow
        displayRights={data?.authorization.displayRights ?? null}
        metaState={metaState}
        authorizationId={data?.authorization.current?.id ?? null}
        authorizationStatus={data?.authorization.current?.status}
        onRenew={setProposalAuthorizationId}
      />
    </Box>
  );

  // A relaunchable launched boost (failed or stopped) is a dead-end at this level
  // unless the footer offers a way back into setup: the delivery view is read-only
  // and its status footer has no control. When the brand can reconfigure (eligible
  // and allowed), the footer becomes an action that reopens setup; otherwise it
  // stays the honest delivery status.
  const canRelaunchFromDelivery =
    !!launchedBoost && isBoostRelaunchable(launchedBoost.deliveryState) && canOpenSetup;
  // A historical boost must not hide the one action that can repair lapsed
  // consent. Stopped/failed delivery is relaunchable in principle, but an
  // expired or revoked authorization needs a new creator request before setup.
  const canRequestAuthorizationFromDelivery =
    !!launchedBoost &&
    isBoostRelaunchable(launchedBoost.deliveryState) &&
    canOpenPreflight &&
    (data?.status === MentionBoostEligibilityStatus.EXPIRED ||
      data?.status === MentionBoostEligibilityStatus.REVOKED);
  // The footer holds a single slot. Relaunch (failed / stopped) and stop
  // (publishing / active / needs attention) are disjoint, so the relaunch branch
  // still wins where it applies; stop only ever fills a slot relaunch never does.
  // The stop confirm dialog is bundled with the footer node so it renders in both
  // the render-prop and default paths, wherever the footer renders.
  const footer = isStoryNotBoostable ? null : (
    <>
      <BoostFooter
        intent={
          launchedBoost
            ? canRequestAuthorizationFromDelivery
              ? {
                  mode: 'action',
                  labelKey: 'mentions.boost.confirm.requestCta',
                  onClick: handleBoostClick,
                }
              : canRelaunchFromDelivery
                ? {
                    mode: 'action',
                    labelKey: 'mentions.boost.relaunchCta',
                    onClick: handleBoostClick,
                  }
                : resolveDeliveryFooterIntent(launchedBoost.deliveryState, stopFlow.openConfirm)
            : resolveEligibilityFooterIntent({
                status: data?.status,
                canManageBoost: !!data?.meta.canManageBoost,
                isActionable: canOpenPreflight || canOpenSetup,
                isLoading,
                isError,
                onAction: handleBoostClick,
              })
        }
      />
      <StopBoostConfirmDialog
        isOpen={stopFlow.isConfirmOpen}
        isStopping={stopFlow.isStopping}
        onConfirm={stopFlow.confirmStop}
        onCancel={stopFlow.cancelConfirm}
        parentCampaignName={launchedBoost?.config.parentCampaignName ?? null}
      />
    </>
  );

  const requestPanel = (
    <MentionBoostRequestPanel
      contentOnlyOptionAvailable={contentOnlyOptionAvailable}
      identity={identity}
      limitToMention={limitToMention}
      onConfirm={handleRequestAuthorization}
      onLimitToMentionChange={setLimitToMention}
      submitting={isRequestingAuthorization}
    />
  );

  const setupPanel = (
    <MentionBoostSetupPanel
      isOpen={isSetupOpen}
      mentionId={mentionId}
      creatorUsername={identity?.username}
      onClose={handleCloseSetup}
    />
  );

  const proposalPanel = proposalAuthorizationId ? (
    <MentionBoostProposalStep
      authorizationId={proposalAuthorizationId}
      creatorHandle={identity?.username}
      metaState={metaState}
      scope={data?.authorization.current?.scope}
      onClose={handleCloseProposal}
    />
  ) : null;

  if (children) {
    return (
      <>
        {children({
          footer,
          isRequestOpen,
          isSetupOpen,
          isBoosted,
          boostLaunchedAt: launchedBoost?.launchedAt ?? null,
          onCloseRequest: handleCloseRequest,
          onCloseSetup: handleCloseSetup,
          onCloseProposal: handleCloseProposal,
          panel,
          requestPanel,
          setupPanel,
          proposalPanel,
        })}
      </>
    );
  }

  return (
    <>
      {isRequestOpen
        ? requestPanel
        : isProposalOpen
          ? proposalPanel
          : isSetupOpen
            ? setupPanel
            : panel}
      {!isRequestOpen && !isSetupOpen && !isProposalOpen && footer}
    </>
  );
}

/**
 * The whole co-publicite surface for a story: one sentence. Muted, because it
 * states a property of the format rather than something that went wrong, and
 * bare, because there is no next step to offer.
 */
function MentionBoostStoryNotice() {
  const { t } = useTranslation();

  return (
    <Box py={5} borderTop="1px solid" borderColor="gray.100">
      <Text fontSize="sm" color="gray.500" lineHeight="tall">
        {t('mentions.boost.storyNotBoostable')}
      </Text>
    </Box>
  );
}

interface MentionBoostContentProps {
  badgeKey: BoostBadgeKey;
  blockers: MentionBoostEligibilityBlocker[];
  copyKey: BoostCopyKey;
  identity?: BoostIdentity;
  isLoading: boolean;
  panelTone: BoostPanelTone;
  showAdminWarning: boolean;
  /** The brand has no Meta Ads connection, so a request would lead nowhere. */
  showAdsNotConnected: boolean;
}

function MentionBoostContent({
  badgeKey,
  blockers,
  copyKey,
  identity,
  isLoading,
  panelTone,
  showAdminWarning,
  showAdsNotConnected,
}: MentionBoostContentProps) {
  const { t } = useTranslation();

  return (
    <Box py={5} borderTop="1px solid" borderColor="gray.100">
      <HStack justify="space-between" mb={3} gap={3}>
        <Heading size="sm" color="gray.900">
          {t('mentions.boost.title')}
        </Heading>
        <Badge colorPalette={panelTone} variant="subtle" textTransform="none" flexShrink={0}>
          {t(`mentions.boost.status.${badgeKey}`)}
        </Badge>
      </HStack>

      <Box border="1px solid" borderColor="gray.100" rounded="lg" overflow="hidden" bg="white">
        {badgeKey === 'pending' && identity ? (
          <Box px={4} py={5}>
            <BoostAuthorizationHandshake
              {...identity}
              icon="hourglass"
              title={t('mentions.boost.pendingHandshake')}
              description={t('mentions.boost.copy.pending')}
            />
          </Box>
        ) : (
          <Box bg={`${panelTone}.50`} px={3} py={3}>
            {isLoading ? (
              <Skeleton height="18px" maxW="full" />
            ) : (
              <Text fontSize="sm" color={`${panelTone}.800`} lineHeight="tall">
                {t(`mentions.boost.copy.${copyKey}`)}
              </Text>
            )}
          </Box>
        )}

        <VStack align="stretch" gap={0}>
          {blockers.map((blocker, index) => (
            <BoostBlockerRow blockerCode={blocker.code} key={`${blocker.code}-${index}`} />
          ))}

          {showAdsNotConnected && <MetaAdsNotConnectedAlert />}

          {showAdminWarning && (
            <Alert
              status="warning"
              variant="surface"
              m={3}
              title={t('mentions.boost.adminRequired.title')}
              icon={<WarningTriangle width={16} height={16} />}
            >
              {t('mentions.boost.adminRequired.description')}
            </Alert>
          )}
        </VStack>
      </Box>
    </Box>
  );
}

/**
 * The one blocker that names an account rather than a problem.
 *
 * A warning triangle says something is wrong; the Meta mark says WHICH account
 * to go and connect, which is the whole of what the brand has to do here. The
 * other blockers keep the generic row: none of them is fixed by connecting
 * something.
 */
function MetaAdsNotConnectedAlert() {
  const { t } = useTranslation();

  return (
    <Alert
      status="warning"
      variant="surface"
      m={3}
      title={t('mentions.boost.adsNotConnected.title')}
      icon={<Image src="/images/integrations/meta.svg" boxSize="16px" alt="" />}
    >
      <VStack align="stretch" gap={2}>
        <Text>{t('mentions.boost.adsNotConnected.description')}</Text>
        <ConnectMetaAdsButton />
      </VStack>
    </Alert>
  );
}

function BoostBlockerRow({ blockerCode }: { blockerCode: MentionBoostEligibilityBlockerCode }) {
  const { t } = useTranslation();
  // Missing ad permissions is fixed by connecting an account, so the row names
  // that account (the Meta mark) and offers the way there; every other blocker
  // keeps the plain warning, since none of them is fixed by connecting anything.
  const isMetaAccess = blockerCode === MentionBoostEligibilityBlockerCode.META_PERMISSIONS_MISSING;

  return (
    <Flex gap={2} px={3} py={3} align="flex-start" borderTop="1px solid" borderColor="gray.100">
      <Box color={`${BLOCKER_COLOR[blockerCode]}.600`} pt={0.5} flexShrink={0}>
        {isMetaAccess ? (
          <Image src="/images/integrations/meta.svg" boxSize="16px" alt="" />
        ) : (
          <WarningTriangle width={16} height={16} />
        )}
      </Box>
      <VStack align="stretch" gap={2} minW={0}>
        <Box>
          <Text fontSize="sm" color="gray.800" fontWeight="medium">
            {t(`mentions.boost.blockers.${blockerCode}.title`)}
          </Text>
          <Text fontSize="xs" color="gray.500">
            {t(`mentions.boost.blockers.${blockerCode}.description`)}
          </Text>
        </Box>
        {isMetaAccess && <ConnectMetaAdsButton />}
      </VStack>
    </Flex>
  );
}
